import type { PropsWithChildren, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AdminLlmSource, AdminReadingListItem, PaymentStatus, SpreadType } from '../../lib/types';
import UiIcon from '../../components/common/UiIcon';

const dateTimeFormatter = new Intl.DateTimeFormat('ko-KR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function formatDateTime(value?: string): string {
  if (!value) return '-';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return dateTimeFormatter.format(new Date(parsed));
}

export function formatTokenCount(value?: number): string {
  if (!value) return '0';
  return Math.round(value).toLocaleString('ko-KR');
}

export function formatUsdCost(value?: number): string {
  if (!value) return '$0.000000';
  return `$${value.toFixed(6)}`;
}

const ESTIMATED_USD_TO_KRW = 1400;

export function formatEstimatedLlmCost(value?: number): string {
  const usd = formatUsdCost(value);
  const krw = Math.max(0, Math.round((value || 0) * ESTIMATED_USD_TO_KRW));
  return `${usd} / 약 ${krw.toLocaleString('ko-KR')}원`;
}

export function formatSpreadLabel(spread?: SpreadType): string {
  switch (spread) {
    case 'one-card':
      return '원카드';
    case 'three-card':
      return '쓰리카드';
    case 'celtic-cross':
      return '켈틱 크로스';
    default:
      return '미확인';
  }
}

export function formatSourceLabel(source?: AdminLlmSource): string {
  switch (source) {
    case 'live':
      return '실응답';
    case 'fallback':
      return '대체 응답';
    default:
      return '미확인';
  }
}

export function formatPaymentStatusLabel(status?: PaymentStatus): string {
  switch (status) {
    case 'paid':
      return '결제 완료';
    case 'failed':
      return '결제 실패';
    default:
      return '결제 대기';
  }
}

export function formatGenerationStatusLabel(status?: string): string {
  switch (status) {
    case 'completed':
      return '생성 완료';
    case 'failed':
      return '생성 실패';
    case 'generating':
      return '생성 중';
    default:
      return '생성 대기';
  }
}

export function shortId(value: string): string {
  return value.length > 8 ? value.slice(0, 8) : value;
}

export function compactText(value?: string, length = 88): string {
  if (!value) return '-';
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= length) return normalized;
  return `${normalized.slice(0, length - 3)}...`;
}

type BadgeTone = 'default' | 'success' | 'warning' | 'danger' | 'muted' | 'accent';

function toneClasses(tone: BadgeTone) {
  switch (tone) {
    case 'success':
      return 'border-emerald-500/35 bg-emerald-500/10 text-emerald-200';
    case 'warning':
      return 'border-amber-500/35 bg-amber-500/10 text-amber-100';
    case 'danger':
      return 'border-rose-500/35 bg-rose-500/10 text-rose-200';
    case 'accent':
      return 'border-sky-500/35 bg-sky-500/10 text-sky-100';
    case 'muted':
      return 'border-[#7e6047]/30 bg-black/20 text-[#d8bfa1]/70';
    default:
      return 'border-[#8a6a4b]/40 bg-[#241913]/70 text-[#f4e2cb]';
  }
}

export function AdminBadge({
  label,
  tone = 'default',
}: {
  label: string;
  tone?: BadgeTone;
}) {
  return (
    <span className={`inline-flex items-center border px-2.5 py-1 text-[11px] font-medium tracking-[0.14em] uppercase ${toneClasses(tone)}`}>
      {label}
    </span>
  );
}

export function AdminPanel({
  title,
  subtitle,
  actions,
  children,
}: PropsWithChildren<{
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}>) {
  return (
    <section className="border border-[#8a6a4b]/35 bg-[#17110f]/85 p-5 shadow-[0_18px_60px_rgba(0,0,0,0.25)] backdrop-blur-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#f3e2cc]">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-[#d8bfa1]/72">{subtitle}</p>}
        </div>
        {actions}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function AdminStatCard({
  label,
  value,
  caption,
}: {
  label: string;
  value: string | number;
  caption?: string;
}) {
  return (
    <div className="border border-[#8a6a4b]/30 bg-[#201611]/80 p-4">
      <p className="text-[11px] uppercase tracking-[0.22em] text-[#c9a092]/75">{label}</p>
      <p className="mt-3 text-3xl font-semibold text-[#f5e5d0]">{value}</p>
      {caption && <p className="mt-2 text-sm leading-6 text-[#d8bfa1]/70">{caption}</p>}
    </div>
  );
}

export function AdminMetaGrid({ children }: PropsWithChildren) {
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

export function AdminMetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="border border-[#7e6047]/25 bg-black/15 p-3">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a092]/72">{label}</p>
      <div className="mt-2 text-sm leading-6 text-[#f3e2cc]">{value}</div>
    </div>
  );
}

export function AdminEmptyState({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="border border-dashed border-[#8a6a4b]/35 bg-black/10 px-4 py-8 text-center">
      <p className="text-sm font-medium text-[#f3e2cc]">{title}</p>
      <p className="mt-2 text-sm text-[#d8bfa1]/70">{body}</p>
    </div>
  );
}

export function AdminCodeBlock({ value }: { value: string }) {
  return (
    <pre className="max-h-[420px] overflow-auto border border-[#8a6a4b]/25 bg-[#0d0907] p-4 text-xs leading-6 text-[#dbc6aa]">
      {value}
    </pre>
  );
}

export function toneForPayment(status: PaymentStatus): BadgeTone {
  switch (status) {
    case 'paid':
      return 'success';
    case 'failed':
      return 'danger';
    default:
      return 'warning';
  }
}

export function toneForGeneration(status: string): BadgeTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'generating':
      return 'accent';
    default:
      return 'warning';
  }
}

export function toneForSource(source: AdminLlmSource): BadgeTone {
  switch (source) {
    case 'live':
      return 'success';
    case 'fallback':
      return 'warning';
    default:
      return 'muted';
  }
}

export function AdminActionButton({
  to,
  label,
  icon = 'arrow-right',
}: {
  to: string;
  label: string;
  icon?: 'arrow-right' | 'history' | 'home' | 'book';
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-3 py-2 text-sm text-[#f3dfc5] transition-colors hover:bg-[#2d2019]"
    >
      <span>{label}</span>
      <UiIcon name={icon} className="h-4 w-4" />
    </Link>
  );
}

export function AdminReadingCard({
  reading,
  to,
  extra,
}: {
  reading: AdminReadingListItem;
  to: string;
  extra?: ReactNode;
}) {
  return (
    <article className="border border-[#8a6a4b]/30 bg-[#140f0d]/90 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <AdminBadge label={formatSpreadLabel(reading.spread)} tone="muted" />
            <AdminBadge label={formatPaymentStatusLabel(reading.payment_status)} tone={toneForPayment(reading.payment_status)} />
            <AdminBadge label={formatGenerationStatusLabel(reading.generation_status)} tone={toneForGeneration(reading.generation_status)} />
            <AdminBadge label={formatSourceLabel(reading.llm_source)} tone={toneForSource(reading.llm_source)} />
            {reading.expired && <AdminBadge label="만료됨" tone="danger" />}
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#f4e2cb]">
              {reading.question ? compactText(reading.question, 96) : `리딩 ${shortId(reading.id)}`}
            </h3>
            <p className="mt-1 text-sm text-[#d8bfa1]/72">
              {reading.user_email || '게스트'} · {formatDateTime(reading.created_at)}
            </p>
          </div>
          {extra}
          {reading.llm_usage && (
            <p className="text-xs text-[#d8bfa1]/65">
              tokens {formatTokenCount(reading.llm_usage.totalTokens)} · est {formatEstimatedLlmCost(reading.llm_estimated_cost_usd)}
            </p>
          )}
        </div>
        <AdminActionButton to={to} label="상세 보기" />
      </div>
    </article>
  );
}
